import { memo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Breadcrumb from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

import { useConnectionStore } from "stores/ConnectionStore";

import { FOTS } from "utility/typescript";

import ServiceConnectionInstructions from "components/ServiceConnectionInstructions";
import ServiceConnectionStep from "components/ServiceConnectionStep";

// Disable this check so we match the backend regex exactly.
// eslint-disable-next-line no-useless-escape
const ZAPIER_WEBHOOK_REGEX =
  // eslint-disable-next-line no-useless-escape
  /^(?:https:\/\/)(?:[^@\/\n]+@)?(?:www\.)?hooks.zapier.com\/hooks\/catch\/[0-9]{12}\/[a-z0-9]{32}/;

const ZapierWebhookConnectSetupPage = memo(
  function ZapierWebhookConnectSetupPage() {
    const [, connectionActions] = useConnectionStore();

    const [webhookUrl, setWebhookUrl] = useState("");
    const [inputError, setInputError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [complete, setComplete] = useState(false);
    const [uuid, setUuid] = useState("");

    const connect = () => {
      setLoading(true);
      connectionActions.zapierWebhookConnect({
        webhookUrl,
        onSuccess,
        onError,
      });
    };

    const reset = () => {
      setWebhookUrl("");
      setInputError(false);
    };

    const onSuccess = (data: any) => {
      setLoading(false);
      toast.success("Successfully connected Zapier Webhook", {
        position: "bottom-center",
      });
      connectionActions.loadConnections({ reload: true });
      setComplete(true);
      setUuid(data.uuid);
    };

    const onError = () => {
      setLoading(false);
      toast.error("Something went wrong connecting Zapier Webhook", {
        position: "bottom-center",
      });
    };

    return (
      <>
        <Breadcrumbs>
          <Breadcrumb
            underline="none"
            color="inherit"
            component={Link}
            to="/environment/destinations"
          >
            Environment
          </Breadcrumb>
          <Breadcrumb
            underline="none"
            color="inherit"
            component={Link}
            to="/environment/destinations"
          >
            Destinations
          </Breadcrumb>
          <span />
        </Breadcrumbs>

        <ServiceConnectionInstructions
          label="Zapier Webhook Connection"
          forceCollapse={complete ? new Date() : undefined}
        >
          <Stack spacing={2}>
            <Typography variant="body1" fontWeight="bold">
              How ThreatKey Zapier Webhooks work:
            </Typography>
            <Typography variant="body1">
              ThreatKey Zapier webhooks are a way for the ThreatKey platform to
              communicate with a Zapier workflow in near real-time. When an
              event happens in ThreatKey, a webhook is sent to the Zap with
              information about the event. The Zap can then take action based on
              the event.
            </Typography>
            <Typography variant="body1">
              You can use Zapier webhooks to connect to Zapier workflows, to
              receive near real-time alerts of security events.
            </Typography>
            <Typography variant="body1">
              ThreatKey Zapier Webhooks can be used with a variety of
              integrations offered on the Zapier platform. The Zap can then send
              the security alerts to a variety of different services, such as
              Google Sheets, Ops Genie, and Trello.
            </Typography>
          </Stack>
        </ServiceConnectionInstructions>

        {!complete ? (
          <ServiceConnectionStep disabled={loading}>
            <Typography variant="body1">
              Please paste the <strong>Zapier Webhook URL</strong> that you
              would like to connect to.
            </Typography>
            <TextField
              fullWidth
              label="Zapier Webhook URL"
              variant="outlined"
              sx={{ mt: 1 }}
              value={webhookUrl}
              placeholder="https://hooks.zapier.com/hooks/catch/123456789123/aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa/"
              onChange={(e: FOTS) => {
                const webhook = e?.target?.value.trim();
                const webhookValid = !!webhook.match(ZAPIER_WEBHOOK_REGEX);

                setInputError(!!webhook && !webhookValid);
                setWebhookUrl(webhook);
              }}
              error={inputError}
              helperText={inputError ? "Invalid Zapier Webhook URL" : ""}
              data-private="lipsum"
            />
            <Stack sx={{ mt: 2 }} spacing={2} direction="row">
              <LoadingButton
                variant="contained"
                onClick={connect}
                loading={loading}
                disabled={!webhookUrl || inputError}
              >
                Connect Zapier Webhook
              </LoadingButton>
              <Button variant="text" onClick={reset}>
                Clear
              </Button>
            </Stack>
          </ServiceConnectionStep>
        ) : (
          <ServiceConnectionStep disabled={false}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CheckCircleOutlineRoundedIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="body1">Done!</Typography>
            </Box>
            <Stack sx={{ mt: 2 }} spacing={1} direction="column">
              <Typography variant="body1">
                <Link
                  to={`/environment/destinations/zapier_webhook/${uuid}/notifications`}
                >
                  Click here
                </Link>{" "}
                to go to your Zapier Webhook notification settings.
              </Typography>
            </Stack>
          </ServiceConnectionStep>
        )}
      </>
    );
  }
);

export default ZapierWebhookConnectSetupPage;
